import React, { useEffect, useState } from 'react'
import { db } from '../../../firebase'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import Icon from '../../../components/AppIcon'
import Button from '../../../components/ui/Button'
import { logGoldEvent } from '../lib/goldAudit'

const TEXT_FIELDS = [
  { key: 'companyName', label: 'Company name', placeholder: 'Your Company LLC' },
  { key: 'companyAddress', label: 'Address', placeholder: '159 Main St, Everett-MA 02149' },
  { key: 'companyPhone', label: 'Phone', placeholder: '+1 617 000 0000' },
  { key: 'companyEmail', label: 'Email', placeholder: 'contact@yourcompany.com' },
  { key: 'companyTaxId', label: 'Tax ID (optional)', placeholder: 'EIN / Tax ID' },
]

const RISS_SHOP_FIELDS = [
  ['name', 'Shop name'], ['street', 'Street address'], ['city', 'City'],
  ['state', 'State'], ['zip', 'ZIP / postal code'], ['phone', 'Phone (optional)'],
  ['email', 'Email (optional)'], ['contactName', 'Contact person (optional)'],
]

/**
 * Contract Template editor — company info, terms and footer printed on every
 * gold purchase contract (gold_settings/contract_template, migrated from the
 * shop app). Admin-only by Firestore rules.
 */
export default function ExchangeContractTemplate() {
  const [form, setForm] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, 'gold_settings', 'contract_template'))
        const d = snap.exists() ? snap.data() : {}
        if (alive) setForm({
          companyName: d.companyName ?? '',
          companyAddress: d.companyAddress ?? '',
          companyPhone: d.companyPhone ?? '',
          companyEmail: d.companyEmail ?? '',
          companyTaxId: d.companyTaxId ?? '',
          termsAndConditions: d.termsAndConditions ?? '',
          footerText: d.footerText ?? '',
          rissShop: Object.fromEntries(RISS_SHOP_FIELDS.map(([key]) => [key, d.rissShop?.[key] || ''])),
        })
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load template')
      }
    })()
    return () => { alive = false }
  }, [])

  const update = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setSaved(false) }

  const save = async () => {
    setSaving(true); setError(''); setSaved(false)
    try {
      await setDoc(doc(db, 'gold_settings', 'contract_template'), {
        ...form,
        updatedAt: serverTimestamp(),
      }, { merge: true })
      logGoldEvent('contract_template_updated', { companyName: form.companyName })
      setSaved(true)
    } catch (e) {
      setError(e?.message || 'Failed to save (admin only)')
    } finally {
      setSaving(false)
    }
  }

  if (error && !form) {
    return <div className="bg-card rounded-2xl border border-border p-6 text-sm text-destructive">{error}</div>
  }
  if (!form) {
    return <div className="bg-card rounded-2xl border border-border p-8 text-center text-muted-foreground text-sm">Loading template…</div>
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 max-w-2xl">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
          <Icon name="FileText" size={22} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Contract Template</h2>
          <p className="text-sm text-muted-foreground">
            Company info, terms and footer printed on every purchase contract
          </p>
        </div>
      </div>

      <fieldset disabled={saving} className="space-y-4 disabled:opacity-70">
        {TEXT_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-foreground mb-1">{f.label}</label>
            <input
              type="text"
              value={form[f.key]}
              placeholder={f.placeholder}
              onChange={(e) => update(f.key, e.target.value)}
              className="w-full h-11 md:h-10 px-3 rounded-lg border border-border bg-input text-base md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        ))}

        <div>
          <details className="rounded-lg border border-border p-4 mb-4">
            <summary className="cursor-pointer text-sm font-medium">RISS shop information</summary>
            <p className="text-xs text-muted-foreground mt-2">XML reports require shop name, street, city, state and ZIP as separate fields. Confirm the actual shop address; it is not inferred from the contract address. Access credentials are configured on the server, never here.</p>
            <div className="grid gap-3 mt-3 sm:grid-cols-2">
              {RISS_SHOP_FIELDS.map(([key, label]) => <label key={key} className="text-sm">{label}<input aria-label={`RISS shop ${key}`} type="text" value={form.rissShop?.[key] || ''} onChange={e => update('rissShop', { ...form.rissShop, [key]: e.target.value })} className="mt-1 w-full h-11 rounded-lg border border-border bg-input px-3 text-base md:text-sm" /></label>)}
            </div>
          </details>
          <label className="block text-sm font-medium text-foreground mb-1">Terms &amp; conditions</label>
          <textarea
            rows={5}
            value={form.termsAndConditions}
            onChange={(e) => update('termsAndConditions', e.target.value)}
            placeholder="The seller confirms that the gold items sold are owned by them…"
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-input text-base md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <p className="text-xs text-muted-foreground mt-1">Shown on the contract above the signatures.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Footer text</label>
          <input
            type="text"
            value={form.footerText}
            onChange={(e) => update('footerText', e.target.value)}
            placeholder="Thank you for your business!"
            className="w-full h-11 md:h-10 px-3 rounded-lg border border-border bg-input text-base md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </fieldset>

      {error && <p className="text-sm text-destructive mt-4">{error}</p>}

      <div className="flex items-center gap-3 mt-6">
        <Button variant="default" onClick={save} loading={saving} disabled={saving} iconName="Check" iconPosition="left">
          Save template
        </Button>
        {saved && <span className="text-sm text-success flex items-center gap-1"><Icon name="CheckCircle" size={16} /> Saved — new contracts will use it</span>}
      </div>
    </div>
  )
}
